
import { useDraggable } from "@dnd-kit/core";
import { GripVertical, Pencil } from "lucide-react";
import { useToast } from "@/components/Toast";
import { MonDateInput } from "@/components/WeekStepper";
import { useModal } from "@/hooks/useModal";
import { useOffices } from "@/hooks/useOffices";
import { useRoles } from "@/hooks/useRoles";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import { useRecentlyChanged, useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useDealStages } from "@/contexts/DealStagesContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { db } from "@/lib/supabase";
import { diffRecords, recordAudit } from "@/lib/audit";
import { assignmentAuditLabel, projectAuditLabel } from "@/utils/auditLabels";
import { withWeeklyHoursSummary } from "@/utils/auditWeeklyHours";
import type { Database } from "@/types/database.types";
import { computeNumWeeks, computeProjectEndDate, formatUTCDateMDY, parseISODate, currentWeekStartISO } from "@/utils/dateUtils";
import { formatProjectDisplayName, type ProjectReindexResult } from "@/utils/projectUtils";
import { getEffectiveProjectColor, isValidHexColor } from "@/utils/projectColor";
import { getStartOfWeek } from "@/utils/weekUtils";
import { ProjectColorPicker } from "./ProjectColorPicker";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfirmClearAllAssignmentsModal } from "./ConfirmClearAllAssignmentsModal";
import { ConfirmDeleteProjectModal } from "./ConfirmDeleteProjectModal";
import { ConfirmHardDeleteProjectModal } from "./ConfirmHardDeleteProjectModal";
import { EditProjectModal } from "./EditProjectModal";
import { ProjectAssignments } from "./ProjectAssignments";
import type { ProjectAvailabilityFocus } from "./ProjectAvailabilityDock";
import { useStickyProjectHeader } from "./StickyProjectHeaderContext";
import type {
  ProjectMetaHeaderRowProps,
  ProjectSummaryColumnProps,
} from "./ProjectSummaryColumn";

function parseLikelihoodToClose(raw: string): number | null {
    if (!raw.trim()) return null;
    if (!/^\d+$/.test(raw)) return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    if (!Number.isInteger(value)) return null;
    if (value < 0 || value > 100) return null;
    return value;
}

function formatLikelihoodToClose(value: number): string {
    return `${value}%`;
}


interface ProjectRowProps {
    project: ProjectSummary;
    timelineStartDate: string;
    onRefresh: () => void;
    updateProjectOptimistically: (
        projectId: string,
        updates: Partial<ProjectSummary>
    ) => void;
    refreshSilently: () => Promise<void>;
    overAllocatedEmployeeIds: Set<string>;
    employeeProjectLabels?: Map<string, string[]>;
    expanded: boolean;
    onToggleExpanded: (projectId: string) => void;
    onOpenAvailabilityForAssignment?: (focus: ProjectAvailabilityFocus) => void;
    focusedAvailabilityAssignmentId?: string | null;
}

/**
 * Row component displaying a single project with inline editing
 */
function ProjectRowComponent({
    project,
    timelineStartDate,
    onRefresh,
    updateProjectOptimistically,
    refreshSilently,
    overAllocatedEmployeeIds,
    employeeProjectLabels,
    expanded,
    onToggleExpanded,
    onOpenAvailabilityForAssignment,
    focusedAvailabilityAssignmentId,
}: ProjectRowProps) {

    const isOptimisticSaving = project.optimisticStatus === "saving";
    const isOptimisticCreated = project.optimisticStatus === "created";
    const rowElementRef = useRef<HTMLElement | null>(null);

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: project.id,
        data: { projectId: project.id, category_id: project.category_id },
    });

    const setProjectRowRef = useCallback((node: HTMLElement | null) => {
        rowElementRef.current = node;
        setNodeRef(node);
    }, [setNodeRef]);

    useEffect(() => {
        if (!isOptimisticSaving || !rowElementRef.current) return;
        const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        rowElementRef.current.scrollIntoView({
            behavior: reducedMotion ? "auto" : "smooth",
            block: "nearest",
        });
        rowElementRef.current.focus({ preventScroll: true });
    }, [isOptimisticSaving]);

    // Register this row (expanded OR collapsed) as a sticky-header sentinel via
    // a tiny marker at the very top of the card. Tracking every row — not just
    // expanded ones — lets an open project's floating header detach the moment
    // ANY following project crosses the sticky line, instead of waiting for the
    // next expanded project. A 1px marker (vs the whole card) crosses the
    // IntersectionObserver line cleanly even for projects taller than the
    // viewport.
    const stickyHeader = useStickyProjectHeader();
    const registerSticky = stickyHeader?.register;
    const unregisterSticky = stickyHeader?.unregister;
    const setStickySentinelRef = useCallback(
        (node: HTMLElement | null) => {
            if (node) registerSticky?.(project.id, node);
            else unregisterSticky?.(project.id);
        },
        [registerSticky, unregisterSticky, project.id],
    );

    const { addToast } = useToast();
    const { markSelfEdit } = useRecentlyChangedRowsApi();
    const { formatRole } = useRoles();
    const { dealStages, byId: dealStagesById, wonStage, lostStage } = useDealStages();
    const { categories, byId: categoriesById } = useCategories();

    // Label used for audit entries; flags archived projects so the audit log
    // makes it obvious that an archived project was edited.
    const auditLabel = useMemo(() => projectAuditLabel(project), [project]);

    // Inline editing states
    const [editingField, setEditingField] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [projectModalMode, setProjectModalMode] = useState<"edit" | "duplicate">("edit");

    // Form state for inline editing
    const [formData, setFormData] = useState<{
        name: string;
        office_id: string | null;
        deal_stage_id: string;
        likelihood_to_close: string;
        start_date: string;
        num_weeks: number | null;
        category_id: string;
    }>({
        name: project.name,
        office_id: project.office_id,
        deal_stage_id: project.deal_stage?.id ?? "",
        likelihood_to_close: String(project.likelihood_to_close),
        start_date: project.start_date,
        num_weeks: project.num_weeks,
        category_id: project.category_id,
    });

    // Ref to always have the latest formData (avoids stale closure issues with async saves)
    const formDataRef = useRef(formData);
    formDataRef.current = formData;

    const { offices } = useOffices();

    // Update form data when project changes
    useEffect(() => {
        setFormData({
            name: project.name,
            office_id: project.office_id,
            deal_stage_id: project.deal_stage?.id ?? "",
            likelihood_to_close: String(project.likelihood_to_close),
            start_date: project.start_date,
            num_weeks: project.num_weeks,
            category_id: project.category_id,
        });
    }, [project]);

    const { open: openConfirmDeleteModal } = useModal(
        ConfirmDeleteProjectModal,
        {
            project,
            dealStages,
            onConfirm: () => refreshSilently(),
        },
        {
            title: "Was This Project Lost?",
            size: "small",
        }
    );

    const { open: openConfirmHardDeleteModal } = useModal(
        ConfirmHardDeleteProjectModal,
        {
            project,
            onConfirm: () => refreshSilently(),
        },
        {
            title: "Delete Project",
            size: "small",
        }
    );

    const { open: openClearAllAssignmentsModal } = useModal(
        ConfirmClearAllAssignmentsModal,
        {
            project,
            refreshSilently,
            updateProjectOptimistically,
            formatRole,
        },
        {
            title: "Clear All Assignments?",
            size: "small",
        }
    );

    const { open: openProjectModal } = useModal(
        EditProjectModal,
        {
            project,
            offices,
            dealStages,
            categories,
            onSuccess: () => refreshSilently(),
            formatRole,
            mode: projectModalMode,
            onDuplicateProjectClick: () => setProjectModalMode("duplicate"),
            onDeleteProjectClick: openConfirmHardDeleteModal,
        },
        {
            title: projectModalMode === "duplicate" ? "Create Project" : "Edit Project",
            size: "medium",
        }
    );

    const openEditProjectModal = () => {
        setProjectModalMode("edit");
        openProjectModal();
    };


    const handleSave = async (field: string) => {
        if (saving) return;

        setSaving(true);

        const updateData: Database["public"]["Tables"]["projects"]["Update"] = {};
        const optimisticUpdates: Partial<ProjectSummary> = {};

        // Use ref to get latest formData (avoids stale closure with async saves)
        const currentFormData = formDataRef.current;

        try {
            switch (field) {
                case "name":
                    if (!currentFormData.name.trim()) {
                        console.error("Project name is required");
                        setEditingField(null);
                        setSaving(false);
                        return;
                    }
                    updateData.name = currentFormData.name.trim();
                    optimisticUpdates.name = currentFormData.name.trim();
                    break;
                case "office_id":
                    if (!currentFormData.office_id) {
                        console.error("Office is required");
                        setEditingField(null);
                        setSaving(false);
                        return;
                    }
                    const selectedOffice = offices.find(
                        (o) => o.id === currentFormData.office_id
                    );
                    // Validate that selected office is active
                    if (!selectedOffice || !selectedOffice.is_active) {
                        console.error("Cannot assign project to inactive office");
                        setEditingField(null);
                        setSaving(false);
                        return;
                    }
                    updateData.office_id = currentFormData.office_id;
                    optimisticUpdates.office_id = currentFormData.office_id;
                    optimisticUpdates.office = selectedOffice
                        ? { name: selectedOffice.name, is_active: selectedOffice.is_active }
                        : null;
                    break;
                case "deal_stage": {
                    const stageId = currentFormData.deal_stage_id;
                    if (!stageId) {
                        console.error("Deal stage is required");
                        setEditingField(null);
                        setSaving(false);
                        return;
                    }
                    const stage = dealStagesById.get(stageId);
                    if (!stage) {
                        console.error("Invalid deal stage");
                        setEditingField(null);
                        setSaving(false);
                        return;
                    }
                    updateData.deal_stage_id = stage.id;
                    optimisticUpdates.deal_stage = stage;
                    // Reserved stages force their likelihood (Lost = 0, Won = 100);
                    // advancing to a higher-likelihood stage bumps the project up to its baseline.
                    const currentLtc = parseLikelihoodToClose(currentFormData.likelihood_to_close);
                    let nextLtc: number | null = null;
                    if (stage.kind !== "open") {
                        nextLtc = stage.likelihood_to_close;
                    } else if (currentLtc != null && stage.likelihood_to_close > currentLtc) {
                        nextLtc = stage.likelihood_to_close;
                    }
                    if (nextLtc != null) {
                        updateData.likelihood_to_close = nextLtc;
                        optimisticUpdates.likelihood_to_close = nextLtc;
                        setFormData(prev => ({ ...prev, likelihood_to_close: String(nextLtc) }));
                    }
                    break;
                }
                case "likelihood_to_close": {
                    const ltc = parseLikelihoodToClose(currentFormData.likelihood_to_close);
                    if (ltc === null) {
                        console.error("Likelihood to close must be an integer between 0 and 100");
                        setEditingField(null);
                        setSaving(false);
                        return;
                    }
                    updateData.likelihood_to_close = ltc;
                    optimisticUpdates.likelihood_to_close = ltc;
                    // Bidirectional reserved-stage rule: 0 -> Lost, 100 -> Won.
                    const reserved = ltc === 0 ? lostStage : ltc === 100 ? wonStage : null;
                    if (reserved && reserved.id !== project.deal_stage?.id) {
                        updateData.deal_stage_id = reserved.id;
                        optimisticUpdates.deal_stage = reserved;
                        setFormData(prev => ({ ...prev, deal_stage_id: reserved.id }));
                    }
                    break;
                }
                case "start_date":
                    updateData.start_date = currentFormData.start_date;
                    optimisticUpdates.start_date = currentFormData.start_date;
                    break;
                case "num_weeks": {
                    const numWeeks = currentFormData.num_weeks;
                    if (numWeeks !== null && (numWeeks < 1 || !Number.isInteger(numWeeks))) {
                        addToast("Number of weeks must be a positive integer", "error");
                        setValidationError("num_weeks");
                        setSaving(false);
                        return;
                    }
                    updateData.num_weeks = numWeeks ?? undefined;
                    optimisticUpdates.num_weeks = numWeeks ?? undefined;
                    break;
                }
                case "category":
                    updateData.category_id = currentFormData.category_id;
                    optimisticUpdates.category_id = currentFormData.category_id;
                    break;
            }

            // Update optimistically first (immediate UI update)
            updateProjectOptimistically(project.id, optimisticUpdates);
            setEditingField(null);

            const newNumWeeks = currentFormData.num_weeks;
            const isValidNumWeeks =
                field === "num_weeks" &&
                typeof newNumWeeks === "number" &&
                Number.isFinite(newNumWeeks) &&
                Number.isInteger(newNumWeeks) &&
                newNumWeeks >= 1;

            const isStartDateChange =
                field === "start_date" &&
                currentFormData.start_date !== project.start_date;

            // start_date or num_weeks changes go through the atomic RPC, which
            // updates the project AND reconciles every child assignment's hours
            // array in one transaction (previously an update-then-loop of separate
            // HTTP requests that could half-apply). The RPC reports which
            // assignments it resized so we still record the same audit entries.
            if (isStartDateChange || isValidNumWeeks) {
                // In the num_weeks branch, isValidNumWeeks guarantees a positive integer.
                const patch = isStartDateChange
                    ? { start_date: currentFormData.start_date }
                    : { num_weeks: newNumWeeks as number };
                const oldProjectValues = isStartDateChange
                    ? { start_date: project.start_date }
                    : { num_weeks: project.num_weeks };

                markSelfEdit("projects", project.id);
                const { data: rpcData, error: rpcError } = await db.projects.updateWithReindex(project.id, patch);
                if (rpcError || !rpcData) {
                    console.error("Failed to update project:", rpcError);
                    await refreshSilently();
                    return;
                }
                recordAudit({
                    table: "projects",
                    recordId: project.id,
                    operation: "UPDATE",
                    changes: diffRecords(oldProjectValues, patch),
                    recordLabel: auditLabel,
                    officeId: project.office_id ?? null,
                });

                for (const change of (rpcData as unknown as ProjectReindexResult).resized) {
                    const currentAssignment = project.project_assignments.find(a => a.id === change.id);
                    markSelfEdit("project_assignments", change.id);
                    recordAudit({
                        table: "project_assignments",
                        recordId: change.id,
                        operation: "UPDATE",
                        changes: withWeeklyHoursSummary(
                            { weekly_hours_allocated: { old: change.old, new: change.new } },
                            project.start_date,
                        ),
                        recordLabel: assignmentAuditLabel(
                            auditLabel,
                            currentAssignment ?? { employees: [], required_role_id: null },
                            formatRole,
                        ),
                        officeId: project.office_id ?? null,
                    });
                }
            } else {
                markSelfEdit("projects", project.id);
                const { error } = await db.projects.update(project.id, updateData);
                if (error) {
                    console.error("Failed to update project:", error);
                    await refreshSilently();
                    return;
                }
                // Build the old object from the in-memory project for exactly the
                // fields being updated, so the diff is minimal and accurate.
                const oldProjectData: Record<string, unknown> = {};
                for (const key of Object.keys(updateData)) {
                    oldProjectData[key] = (project as Record<string, unknown>)[key] ?? null;
                }
                recordAudit({
                    table: "projects",
                    recordId: project.id,
                    operation: "UPDATE",
                    changes: diffRecords(oldProjectData, updateData),
                    recordLabel: auditLabel,
                    officeId: project.office_id ?? null,
                });
            }

            // Success - refresh data
            await refreshSilently();
        } catch (err) {
            // Revert on error by refreshing from server
            console.error("Error updating project:", err);
            await refreshSilently();
        } finally {
            setSaving(false);
        }
    };

    // The project's stored color when the picker was opened, captured on the
    // first live preview so the audit diff and no-op check compare against the
    // real pre-edit value rather than an intermediate preview color.
    const colorBeforeEditRef = useRef<string | null | undefined>(undefined);

    // Live color preview while the user drags/types in the picker. Optimistic
    // only — every row (this board + the makers timeline) recolors instantly, but
    // nothing is written to the database, so a drag no longer spams the audit log.
    const handleColorPreview = (hex: string) => {
        if (!isValidHexColor(hex)) return;
        if (colorBeforeEditRef.current === undefined) {
            colorBeforeEditRef.current = project.color ?? null;
        }
        updateProjectOptimistically(project.id, { color: hex });
    };

    // Persist the color once the picker closes. Writes a single row update +
    // audit entry, then syncs to other clients via the existing projects realtime.
    const handleColorCommit = async (hex: string) => {
        const previousColor =
            colorBeforeEditRef.current === undefined
                ? project.color ?? null
                : colorBeforeEditRef.current;
        colorBeforeEditRef.current = undefined;

        if (!isValidHexColor(hex)) return;

        // Ignore no-op commits (opening and closing without a net change) so the
        // auto-generated default isn't needlessly pinned into the DB. Restore the
        // optimistic state to the pre-edit color in case a preview moved it.
        const previousEffective = getEffectiveProjectColor(project.id, previousColor);
        if (hex.toLowerCase() === previousEffective.toLowerCase()) {
            updateProjectOptimistically(project.id, { color: previousColor });
            return;
        }

        updateProjectOptimistically(project.id, { color: hex });

        try {
            markSelfEdit("projects", project.id);
            const { error } = await db.projects.update(project.id, { color: hex });
            if (error) {
                console.error("Failed to update project color:", error);
                await refreshSilently();
                return;
            }
            recordAudit({
                table: "projects",
                recordId: project.id,
                operation: "UPDATE",
                changes: diffRecords({ color: previousColor }, { color: hex }),
                recordLabel: auditLabel,
                officeId: project.office_id ?? null,
            });
        } catch (err) {
            console.error("Error updating project color:", err);
            await refreshSilently();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>, field: string) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSave(field);
        } else if (e.key === "Escape") {
            setEditingField(null);
            // Reset form data to original project values
            setFormData({
                name: project.name,
                office_id: project.office_id,
                deal_stage_id: project.deal_stage?.id ?? "",
                likelihood_to_close: String(project.likelihood_to_close),
                start_date: project.start_date,
                num_weeks: project.num_weeks,
                category_id: project.category_id,
            });
        }
    };

    const hasUnassignedRole = useMemo(() => {
        const category = categoriesById.get(project.category_id);
        return (category?.highlight_unstaffed_roles ?? false) &&
            project.project_assignments.some((a) => a.employees.length === 0);
    }, [project, categoriesById]);

    const isLockedProject = useMemo(() => {
        if (project.deal_stage?.kind !== "won" || project.likelihood_to_close !== 100) return false;
        const currentWeekStart = getStartOfWeek(new Date(Date.now()));
        const projectStart = parseISODate(project.start_date);
        return projectStart.getTime() < currentWeekStart.getTime();
    }, [project.deal_stage?.kind, project.likelihood_to_close, project.start_date]);

    const isFlashing = useRecentlyChanged("projects", project.id);

    const collapsedMetaContent = (
        <div className="project-card__meta">
            <div>
                <span className="project-card__meta-label">Deal Stage: </span>
                {editingField === "deal_stage" ? (
                    <select
                        className="project-card__inline-select project-card__inline-select--small"
                        value={formData.deal_stage_id}
                        onChange={(e) =>
                            setFormData({ ...formData, deal_stage_id: e.target.value })
                        }
                        onBlur={() => handleSave("deal_stage")}
                        onKeyDown={(e) => handleKeyDown(e, "deal_stage")}
                        autoFocus
                        disabled={saving}
                    >
                        {dealStages.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                                {stage.name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span
                        className={isLockedProject ? undefined : "project-card__editable"}
                        onClick={isLockedProject ? undefined : () => setEditingField("deal_stage")}
                    >
                        {project.deal_stage?.name}
                    </span>
                )}
                <span className="divider"> • </span>

                <span className="project-card__meta-label">Likelihood to Close: </span>
                {editingField === "likelihood_to_close" ? (
                    <input
                        type="number"
                        className="project-card__inline-input project-card__inline-input--small"
                        style={{ width: "4rem", minWidth: "4rem" }}
                        value={formData.likelihood_to_close}
                        onChange={(e) =>
                            setFormData({ ...formData, likelihood_to_close: e.target.value })
                        }
                        onBlur={() => handleSave("likelihood_to_close")}
                        onKeyDown={(e) => handleKeyDown(e, "likelihood_to_close")}
                        placeholder="0-100"
                        min="0"
                        max="100"
                        step="1"
                        autoFocus
                        disabled={saving}
                    />
                ) : (
                    <span
                        className={isLockedProject ? undefined : "project-card__editable"}
                        onClick={isLockedProject ? undefined : () => setEditingField("likelihood_to_close")}
                    >
                        {formatLikelihoodToClose(project.likelihood_to_close)}
                    </span>
                )}
                <span className="divider"> • </span>

                <span className="project-card__meta-label">Category: </span>
                {editingField === "category" ? (
                    <select
                        className="project-card__inline-select project-card__inline-select--small"
                        value={formData.category_id}
                        onChange={(e) =>
                            setFormData({ ...formData, category_id: e.target.value })
                        }
                        onBlur={() => handleSave("category")}
                        onKeyDown={(e) => handleKeyDown(e, "category")}
                        autoFocus
                        disabled={saving}
                    >
                        {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span
                        className="project-card__editable"
                        onClick={() => setEditingField("category")}
                    >
                        {categoriesById.get(project.category_id)?.name ?? "—"}
                    </span>
                )}
            </div>
            <div>
                {editingField === "start_date" ? (
                    <MonDateInput
                        minDate={currentWeekStartISO()}
                        value={formData.start_date}
                        onChange={(value) =>
                            setFormData((prev) => ({ ...prev, start_date: value }))
                        }
                        onCommit={() => handleSave("start_date")}
                        disabled={saving}
                        fontSize="0.75rem"
                        autoFocus
                    />
                ) : (
                    <span
                        className={isLockedProject ? undefined : "project-card__editable"}
                        onClick={isLockedProject ? undefined : () => setEditingField("start_date")}
                    >
                        Start: {formatUTCDateMDY(project.start_date)}
                    </span>
                )}
                <span className="divider"> • </span>
                {editingField === "num_weeks" ? (
                    <MonDateInput
                        value={formData.start_date && formData.num_weeks
                            ? computeProjectEndDate(formData.start_date, formData.num_weeks)
                            : undefined}
                        minDate={formData.start_date
                            ? computeProjectEndDate(formData.start_date, 2)
                            : undefined}
                        onChange={(value) => {
                            const weeks = computeNumWeeks(formData.start_date, value);
                            setFormData((prev) => ({ ...prev, num_weeks: weeks }));
                        }}
                        onCommit={() => handleSave("num_weeks")}
                        disabled={saving}
                        fontSize="0.75rem"
                        autoFocus
                    />
                ) : (
                    <span
                        className="project-card__editable"
                        onClick={() => setEditingField("num_weeks")}
                    >
                        End: {project.start_date && project.num_weeks
                            ? formatUTCDateMDY(computeProjectEndDate(project.start_date, project.num_weeks))
                            : "—"}
                    </span>
                )}
            </div>
        </div>
    );

    const composedNameDisplay = formatProjectDisplayName(project.name, project.client);

    const headerRowProps: ProjectMetaHeaderRowProps = {
        projectId: project.id,
        projectName: project.name,
        projectColor: project.color,
        onColorPreview: handleColorPreview,
        onColorCommit: handleColorCommit,
        nameValue: formData.name,
        editingName: editingField === "name",
        onStartNameEdit: () => setEditingField("name"),
        onNameChange: (value) => setFormData((prev) => ({ ...prev, name: value })),
        onNameSave: () => handleSave("name"),
        onNameKeyDown: (e) => handleKeyDown(e, "name"),
        nameSaving: saving,
        onEditClick: openEditProjectModal,
        clientName: project.client,
        expandCollapseButton: (
            <button
                className="project-card__toggle project-card__toggle--open"
                onClick={() => onToggleExpanded(project.id)}
                type="button"
                aria-label="Collapse project"
            />
        ),
        dragHandle: (
            <button
                className="project-card__drag-handle"
                {...listeners}
                {...attributes}
                type="button"
                aria-label="Drag to move project"
            >
                <GripVertical size={14} />
            </button>
        ),
    };

    const projectSummaryColumnProps: ProjectSummaryColumnProps = {
        project,
        formData,
        setFormData,
        editingField,
        setEditingField,
        onSave: handleSave,
        onKeyDown: handleKeyDown,
        validationError,
        saving,
        onClearValidationError: () => setValidationError(null),
        locked: isLockedProject,
        onClearAllAssignments: openClearAllAssignmentsModal,
        headerRowProps,
    };

    return (
        <article
            ref={setProjectRowRef}
            className={`project-card ${expanded ? "project-card--expanded" : ""} ${hasUnassignedRole ? "project-card--over-allocated" : ""} ${isDragging ? "project-card--is-dragging" : ""} ${isFlashing || isOptimisticCreated ? "row--flashing" : ""} ${isOptimisticSaving ? "project-card--saving" : ""}`}
            data-project-id={project.id}
            data-optimistic-status={project.optimisticStatus}
            aria-busy={isOptimisticSaving || undefined}
            aria-label={isOptimisticSaving ? `Saving project ${project.name}` : undefined}
            tabIndex={isOptimisticSaving ? -1 : undefined}
        >
            <div
                ref={setStickySentinelRef}
                aria-hidden="true"
                style={{ height: 1, width: 1, pointerEvents: "none" }}
            />
            {!expanded && (
                <div className="project-card__summary">
                    <div className="project-card__fixed">
                        <button
                            className="project-card__drag-handle"
                            {...listeners}
                            {...attributes}
                            type="button"
                            aria-label="Drag to move project"
                        >
                            <GripVertical size={14} />
                        </button>
                        <button
                            className="project-card__toggle"
                            onClick={() => onToggleExpanded(project.id)}
                            type="button"
                            aria-label="Expand project"
                        />
                        <button
                            className="icon-button"
                            onClick={openEditProjectModal}
                            title="Edit project details"
                            type="button"
                            aria-label="Edit project"
                        >
                            <Pencil size={16} />
                        </button>
                        <ProjectColorPicker
                            projectId={project.id}
                            color={project.color}
                            onPreview={handleColorPreview}
                            onCommit={handleColorCommit}
                            title="Project color"
                            ariaLabel={`Color for ${composedNameDisplay}`}
                        />

                        <div className="project-card__info">
                            <div className="project-card__name-row">
                                {editingField === "name" ? (
                                    <input
                                        type="text"
                                        className="project-card__inline-input"
                                        value={formData.name}
                                        onChange={(e) =>
                                            setFormData({ ...formData, name: e.target.value })
                                        }
                                        onBlur={() => handleSave("name")}
                                        onKeyDown={(e) => handleKeyDown(e, "name")}
                                        autoFocus
                                        disabled={saving}
                                    />
                                ) : (
                                    <div
                                        className="project-card__name project-card__editable"
                                        onClick={() => setEditingField("name")}
                                        title={composedNameDisplay}
                                    >
                                        {composedNameDisplay}
                                    </div>
                                )}
                                {isOptimisticSaving && (
                                    <span className="project-card__save-status" role="status">
                                        <span className="project-card__save-spinner" aria-hidden="true" />
                                        Saving…
                                    </span>
                                )}
                                {isOptimisticCreated && (
                                    <span className="project-card__save-status project-card__save-status--created" role="status">
                                        Saved
                                    </span>
                                )}
                                {collapsedMetaContent}
                                {hasUnassignedRole && (
                                    <span
                                        className="project-card__over-allocated-badge"
                                        title="Unassigned roles"
                                    >
                                        ⚠️
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {expanded ? (
                <ProjectAssignments
                    project={project}
                    timelineStartDate={timelineStartDate}
                    onRefresh={onRefresh}
                    overAllocatedEmployeeIds={overAllocatedEmployeeIds}
                    employeeProjectLabels={employeeProjectLabels}
                    updateProjectOptimistically={updateProjectOptimistically}
                    refreshSilently={refreshSilently}
                    projectSummaryColumnProps={projectSummaryColumnProps}
                    onOpenAvailabilityForAssignment={onOpenAvailabilityForAssignment}
                    focusedAvailabilityAssignmentId={focusedAvailabilityAssignmentId}
                />
            ) : null}
        </article>
    );
}

export const ProjectRow = memo(ProjectRowComponent);
